import { notify } from "@/components/common/Notification";
import { DataOperationsService, UploadService } from "@/services/APIs";
import { Progress, Typography, Upload } from "antd";
import { CheckCircle } from "lucide-react";

const { Text } = Typography;

interface FileUploadStepProps {
  currentStep: number;
  isUploading: boolean;
  selectedSchemaId: string;
  importType: string;
  onFileUpload: (importId: string) => void;
  onStepComplete: () => void;
  setFileColumns: (columns: string[]) => void;
  setIsUploading: (uploading: boolean) => void;
  disabled?: boolean;
}

const FileUploadStep = ({
  currentStep,
  isUploading,
  selectedSchemaId,
  importType,
  onFileUpload,
  onStepComplete,
  setFileColumns,
  setIsUploading,
  disabled = false,
}: FileUploadStepProps) => {
  const handleSetupImport = async (schema: string, documentId: string) => {
    const importResponse = await DataOperationsService.importData(schema, {
      document_id: documentId,
      import_type: importType as "create new records",
    });

    if (importResponse?.import_id) {
      onFileUpload(importResponse.import_id);
      return importResponse.import_id;
    } else {
      notify.error("Import Setup Failed", "No import ID received from server.");
      return null;
    }
  };

  const handleFileUpload = async (file: File) => {
    // Read CSV headers
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const lines = text.split("\n");
      if (lines.length > 0) {
        const headers = lines[0]
          .split(",")
          .map(h => h.trim().replace(/"/g, ""));
        setFileColumns(headers);
      }
    };
    reader.readAsText(file);

    // Upload file
    setIsUploading(true);
    const response = await UploadService.uploadFiles({
      folderName: `importfiles`,
      files: [file],
    });

    let docId;
    if (response?.[0]?.id) {
      docId = response[0].id;
    } else {
      notify.error("Upload Failed", "No document ID received from server.");
      return;
    }

    // Setup import using service
    const importId = await handleSetupImport(selectedSchemaId ?? "", docId);
    if (importId) {
      onStepComplete();
    }
    setIsUploading(false);
  };

  return (
    <div className="space-y-4">
      <Upload.Dragger
        accept=".csv"
        beforeUpload={file => {
          handleFileUpload(file);
          return false;
        }}
        showUploadList={false}
        disabled={isUploading || currentStep > 2 || disabled}
      >
        <div className="flex flex-col gap-4">
          {currentStep > 2 ? (
            <>
              <CheckCircle className="mx-auto text-green-500" size={24} />
              <p className="font-medium text-green-600">
                File Upload Completed
              </p>
              <p className="text-sm text-gray-500">
                File has been successfully uploaded and processed
              </p>
            </>
          ) : (
            <>
              <p>Click or drag CSV file to this area to upload</p>
              <p className="text-sm text-gray-500">
                Support for CSV files only
              </p>
            </>
          )}
        </div>
        {isUploading && (
          <div className="mt-4">
            <Progress percent={75} status="active" strokeColor="#1890ff" />
            <Text type="secondary" className="text-sm">
              Processing file...
            </Text>
          </div>
        )}
      </Upload.Dragger>
    </div>
  );
};

export default FileUploadStep;
